import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useEffect, useState } from "react";
import { Alert } from "antd";
import {
  checkRouteAccess, getCurrentPlan,
  userHasPortalAccess, portalForRoute,
} from "../../utils/portalAccess";
import { isPartnerSsoSession } from "../../utils/embedFlow";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

// Best-effort signal to the host frame that the iframe has fallen out of
// auth context. Same shape as EmbedTokenContext's notifications so a
// listener that handles "noticeai.token_expired" picks this up too. We
// inline the postMessage instead of importing a helper to avoid a cyclic
// dependency on EmbedTokenContext (which itself imports route-aware code).
const notifyHostAuthLost = () => {
  if (typeof window === "undefined" || !window.parent) return;
  if (window.parent === window) return; // not in an iframe
  try {
    window.parent.postMessage({ type: "noticeai.token_expired" }, "*");
  } catch {
    /* swallow — origin restrictions can theoretically block postMessage */
  }
};

const ProtectedRoute = ({ children, portalName = null }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  // Sticky once true: a partner-SSO user who hits ProtectedRoute without
  // an auth surface (i.e. a stray <Link to="/"> escaped /embed/*) lands on
  // an inline expired panel instead of being kicked to /login. The host
  // frame is also pinged so it can mint a fresh JWT and reload the iframe.
  const [embedAuthLost, setEmbedAuthLost] = useState(false);

  useEffect(() => {
    const checkAccess = () => {
      if (!user) {
        // Defence-in-depth for the partner-SSO iframe: if a stray Link
        // pulled the user OUT of /embed/* into a tenant route, ProtectedRoute
        // would otherwise redirect to /login — but a subscribed iframe user
        // must NEVER see NoticeAI's login. Detect via the JWT `flow` claim
        // (the only embed-mode signal that survives leaving the embed tree
        // — partner_id alone is NOT embed-specific) and render an inline
        // session-expired panel + postMessage the host so it can refresh.
        if (isPartnerSsoSession()) {
          setEmbedAuthLost(true);
          setIsChecking(false);
          notifyHostAuthLost();
          return;
        }
        navigate("/login", { replace: true });
      }

      // Review Users (nabs_reviewer) are org-less internal staff opening
      // customer notices from the Review Dashboard. Tenant portal/plan
      // gates don't apply to them (the backend enforces their authority
      // per notice via the service request) — without this bypass the
      // empty enabledPortals list bounced every notice open with a
      // misleading upgrade toast.
      if (user?.role === "nabs_reviewer") {
        setHasAccess(true);
        setIsChecking(false);
        return;
      }

      if (portalName) {
        const enabledPortals = JSON.parse(
          localStorage.getItem("enabledPortals") || "[]"
        );
        const orgAccess = enabledPortals.includes(portalName);
        const userAccess = userHasPortalAccess(portalName);
        const access = orgAccess && userAccess;
        setHasAccess(access);

        if (!access) {
          if (!orgAccess) {
            const currentPlan = getCurrentPlan();
            toast.error(
              `Access to ${portalName} portal is not available in your ${currentPlan} plan. Please upgrade to unlock this feature.`,
              { duration: 5000 }
            );
          } else {
            // Org has it but user-level flag denies — Feature 2.
            toast.error(
              `${portalName} portal access has been disabled for your account. Contact your admin to request access.`,
              { duration: 5000 }
            );
          }
          navigate("/", { replace: true });
          return;
        }
      } else {
        const routeAccess = checkRouteAccess(location.pathname);
        // Layer in user-level check (Feature 2). routeAccess.portalName
        // is the display name ("GST"); pass it through to userHasPortal.
        const userAccess = routeAccess.portalName
          ? userHasPortalAccess(routeAccess.portalName)
          : true;
        const finalAccess = routeAccess.hasAccess && userAccess;
        setHasAccess(finalAccess);

        if (!finalAccess) {
          const portalLabel = routeAccess.displayName || portalForRoute(location.pathname) || "this portal";
          if (!routeAccess.hasAccess && routeAccess.requiredUpgrade) {
            const currentPlan = getCurrentPlan();
            toast.error(
              `Access to ${routeAccess.displayName} is not available in your ${currentPlan} plan. Please upgrade to unlock this feature.`,
              { duration: 5000 }
            );
          } else if (!userAccess) {
            toast.error(
              `${portalLabel} access has been disabled for your account. Contact your admin to request access.`,
              { duration: 5000 }
            );
          }
          navigate("/", { replace: true });
          return;
        }
      }

      setIsChecking(false);
    };

    const timer = setTimeout(checkAccess, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, navigate, portalName, user]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f0f5ff]">
        <motion.div
          className="flex flex-col items-center space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-blue-600 font-medium">Verifying access...</p>
        </motion.div>
      </div>
    );
  }

  if (embedAuthLost) {
    // Embed surface lost its auth context (no `user` AND token isn't on
    // localStorage — only place an embed JWT lives is in-memory inside
    // EmbedTokenContext, which has either unmounted or never mounted on
    // this route). Render a quiet inline panel; the host frame already
    // received a postMessage to refresh.
    return (
      <div className="p-8 max-w-xl mx-auto">
        <Alert
          type="warning"
          showIcon
          message="Session expired"
          description="Please reload this view from your dashboard to continue."
        />
      </div>
    );
  }

  if (hasAccess) {
    return children;
  }

  return null;
};

export default ProtectedRoute;
